import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiClock, FiAlertCircle, FiCalendar, FiPhone,
  FiSearch, FiRefreshCw, FiChevronRight, FiFilter,
  FiX, FiUser, FiTrendingUp, FiDollarSign,
} from 'react-icons/fi';
import { api } from '@crm/shared';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import { toast } from 'react-toastify';
import { fmtDate } from '../utils/dateUtils';
import authService from '../services/authService';
import { useAllUsers } from '../hooks/useAllUsers';
import { canAccessLeadRecord, getReportScopeUserIds, getReportScopeUsers } from '../utils/roleAccess';

/* ─── Design tokens ─── */
const C = {
  navy:'#0D3B5E', navyD:'#082B47', navyL:'#EBF4FF', navyM:'#1A5276',
  gold:'#F5A623', goldL:'#FEF3DC',
  green:'#16A34A', greenL:'#DCFCE7', greenB:'#86EFAC',
  red:'#DC2626', redL:'#FEF2F2', redB:'#FECACA',
  sky:'#0284C7', skyL:'#DBEAFE',
  border:'#E5E7EB', borderL:'#F3F4F6',
  white:'#FFFFFF', t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

/* ─── Helpers ─── */
const todayStr  = () => new Date().toISOString().split('T')[0];
const fmtAmt    = (n) => n ? '₹' + Number(n).toLocaleString('en-IN') : '—';
const extractArr = (r) => { const d = r?.data; return Array.isArray(d?.results) ? d.results : Array.isArray(d?.data) ? d.data : Array.isArray(d) ? d : []; };

const PAGE_SIZE = 20;

/* ─── Shimmer ─── */
const Shimmer = ({ h = 52, r = 8 }) => (
  <div style={{ height:h, borderRadius:r, background:'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)', backgroundSize:'400px 100%', animation:'fuShimmer 1.4s infinite' }}/>
);

/* ─── Stat Pill ─── */
const StatPill = ({ label, value, color, bg, border, active, onClick }) => (
  <div onClick={onClick} style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'12px 18px', borderRadius:12, background: active ? color : bg, border:`1.5px solid ${active ? color : border}`, cursor:'pointer', transition:'all 0.15s', boxShadow: active ? `0 4px 14px ${color}35` : '0 1px 4px rgba(13,59,94,0.06)', minWidth:80 }}
    onMouseEnter={e=>{ if(!active){ e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow=`0 4px 12px ${color}25`; }}}
    onMouseLeave={e=>{ e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow= active ? `0 4px 14px ${color}35` : '0 1px 4px rgba(13,59,94,0.06)'; }}>
    <span style={{ fontSize:22, fontWeight:800, color: active ? '#fff' : C.t1, letterSpacing:'-0.04em', lineHeight:1 }}>{value}</span>
    <span style={{ fontSize:11, fontWeight:600, color: active ? 'rgba(255,255,255,0.75)' : C.t4, marginTop:4, textTransform:'uppercase', letterSpacing:'0.06em', whiteSpace:'nowrap' }}>{label}</span>
  </div>
);

/* ─── Follow-up Row ─── */
const FURow = ({ lead, isLast, navigate }) => {
  const td = todayStr();
  const fuDate = lead.next_follow_up_date;
  const isOverdue  = fuDate && fuDate < td;
  const isToday    = fuDate && fuDate === td;
  const isUpcoming = fuDate && fuDate > td;

  const stage = lead.current_stage || '';
  const STAGE_LABEL = { FOLLOW_UP:'Follow Up', LOGIN:'Login', SANCTION:'Sanction', DISBURSEMENT:'Disbursement', PDD:'PDD', REVENUE:'Revenue', CLOSED:'Closed' };
  const stageLabel = STAGE_LABEL[stage] || 'Follow Up';

  const STAGE_COLOR = {
    FOLLOW_UP:    { bg:C.goldL,   color:'#92670C', border:'#F0D798' },
    LOGIN:        { bg:'#EDE9FE', color:'#6D28D9', border:'#C4B5FD' },
    SANCTION:     { bg:C.skyL,    color:C.sky,     border:'#BAE6FD' },
    DISBURSEMENT: { bg:C.greenL,  color:C.green,   border:C.greenB  },
    PDD:          { bg:C.navyL,   color:C.navy,    border:'#BFDBFE' },
    REVENUE:      { bg:'#CCFBF1', color:'#0F766E', border:'#5EEAD4' },
    CLOSED:       { bg:C.redL,    color:C.red,     border:C.redB    },
  };
  const sc = STAGE_COLOR[stage] || STAGE_COLOR.FOLLOW_UP;

  const fuBg    = isOverdue ? C.redL : isToday ? C.goldL : C.borderL;
  const fuColor = isOverdue ? C.red  : isToday ? '#92670C' : C.t4;
  const fuBdr   = isOverdue ? C.redB : isToday ? '#F0D798' : C.border;

  return (
    <tr onClick={() => navigate(`/leads/${lead.id}`)} style={{ cursor:'pointer', background:isOverdue ? 'rgba(220,38,38,0.018)' : C.white, transition:'background 0.07s', borderBottom: isLast ? 'none' : `1px solid ${C.borderL}` }}
      onMouseEnter={e=>e.currentTarget.style.background= isOverdue ? 'rgba(220,38,38,0.04)' : '#F8FAFC'}
      onMouseLeave={e=>e.currentTarget.style.background= isOverdue ? 'rgba(220,38,38,0.018)' : C.white}>

      {/* Lead */}
      <td style={{ padding:'13px 16px', verticalAlign:'middle', position:'relative' }}>
        <div style={{ position:'absolute', left:0, top:10, bottom:10, width:3, borderRadius:'0 3px 3px 0', background: isOverdue ? C.red : isToday ? C.gold : 'transparent' }}/>
        <div style={{ fontWeight:700, fontSize:13, color:C.navy, fontFamily:'monospace', letterSpacing:'0.04em' }}>{lead.lead_no || `#${lead.id}`}</div>
        <div style={{ fontSize:11.5, color:C.t4, marginTop:2 }}>{fmtDate(lead.lead_date || lead.created_at)}</div>
      </td>

      {/* Customer */}
      <td style={{ padding:'13px 16px', verticalAlign:'middle' }}>
        <div style={{ display:'flex', alignItems:'center', gap:9 }}>
          <div style={{ width:30, height:30, borderRadius:8, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:13, background: isOverdue ? C.redL : C.navyL, color: isOverdue ? C.red : C.navy, border:`1.5px solid ${isOverdue ? C.redB : 'rgba(13,59,94,0.15)'}` }}>
            {(lead.customer_name || '?')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight:700, fontSize:13.5, color:C.t1 }}>{lead.customer_name || '—'}</div>
            {lead.mobile_number && (
              <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, color:C.t3, marginTop:2 }}>
                <FiPhone size={10}/>{lead.mobile_number}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Product */}
      <td style={{ padding:'13px 16px', verticalAlign:'middle', fontSize:13, color:C.t2 }}>
        {lead.product?.name || '—'}
      </td>

      {/* Amount */}
      <td style={{ padding:'13px 16px', verticalAlign:'middle', textAlign:'right', fontSize:13.5, fontWeight:700, color:C.navy, whiteSpace:'nowrap' }}>
        {fmtAmt(lead.loan_amount)}
      </td>

      {/* Stage */}
      <td style={{ padding:'13px 16px', verticalAlign:'middle' }}>
        <span style={{ display:'inline-flex', alignItems:'center', padding:'3px 10px', borderRadius:999, fontSize:11.5, fontWeight:700, background:sc.bg, color:sc.color, border:`1px solid ${sc.border}`, whiteSpace:'nowrap' }}>
          {stageLabel}
        </span>
      </td>

      {/* Follow-up date */}
      <td style={{ padding:'13px 16px', verticalAlign:'middle' }}>
        {fuDate ? (
          <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 11px', borderRadius:999, background:fuBg, border:`1px solid ${fuBdr}`, whiteSpace:'nowrap' }}>
            {isOverdue && <FiAlertCircle size={11} style={{ color:fuColor, flexShrink:0 }}/>}
            {isToday   && <FiClock size={11} style={{ color:fuColor, flexShrink:0 }}/>}
            {isUpcoming && <FiCalendar size={11} style={{ color:fuColor, flexShrink:0 }}/>}
            <span style={{ fontSize:12.5, fontWeight:isOverdue||isToday?700:500, color:fuColor }}>{fmtDate(fuDate)}</span>
            {isOverdue && <span style={{ fontSize:10, fontWeight:800, color:fuColor, textTransform:'uppercase', letterSpacing:'0.05em' }}>OVERDUE</span>}
            {isToday   && <span style={{ fontSize:10, fontWeight:800, color:fuColor, textTransform:'uppercase', letterSpacing:'0.05em' }}>TODAY</span>}
          </div>
        ) : <span style={{ color:C.t4, fontSize:13 }}>—</span>}
      </td>

      {/* Action */}
      <td style={{ padding:'13px 16px', verticalAlign:'middle', textAlign:'center' }}>
        <button onClick={e => { e.stopPropagation(); navigate(`/leads/${lead.id}`); }}
          style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'5px 10px', borderRadius:7, border:`1px solid ${C.border}`, background:C.white, color:C.navy, fontSize:12, fontWeight:600, cursor:'pointer', transition:'all 0.12s' }}
          onMouseEnter={e=>{ e.currentTarget.style.background=C.navyL; e.currentTarget.style.borderColor=C.navy; }}
          onMouseLeave={e=>{ e.currentTarget.style.background=C.white; e.currentTarget.style.borderColor=C.border; }}>
          View <FiChevronRight size={11}/>
        </button>
      </td>
    </tr>
  );
};

/* ─── Responsive hook ─── */
function useWindowWidth() {
  const [w, setW] = React.useState(window.innerWidth);
  React.useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
}

/* ═════════════════════════════════════════
   FOLLOWUPS PAGE
═════════════════════════════════════════ */
export default function Followups() {
  const navigate  = useNavigate();
  const vw        = useWindowWidth();
  const isMobile  = vw < 768;
  const currentUser = authService.getUser();
  const { users: allUsers } = useAllUsers();
  const scopedUsers = getReportScopeUsers(currentUser, allUsers);
  const scopeUserIds = getReportScopeUserIds(scopedUsers);

  const [leads,       setLeads]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [total,       setTotal]       = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search,      setSearch]      = useState('');
  const [activeFilter, setActiveFilter] = useState('today'); // 'overdue' | 'today' | 'upcoming' | 'all'

  const searchRef = React.useRef(search);
  React.useEffect(() => { searchRef.current = search; }, [search]);

  const fetchLeads = useCallback(async (pg = 1, isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const td = todayStr();
      const params = { page_size: 100 };

      if (searchRef.current) params.search = searchRef.current;

      // Apply date filter based on active tab
      if (activeFilter === 'overdue') {
        params.next_follow_up_date_before = td;
        params.current_stage = 'FOLLOW_UP';
      } else if (activeFilter === 'today') {
        params.next_follow_up_date = td;
      } else if (activeFilter === 'upcoming') {
        params.next_follow_up_date_after = td;
      }
      // 'all' — no date filter, fetch all leads that have a follow-up date
      else {
        params.has_follow_up = true;
      }

      const all = [];
      let page = 1;
      while (page <= 50) {
        const res = await api.getLeads({ ...params, page });
        const data = res?.data;
        const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        all.push(...results);
        if (!data?.next || results.length === 0) break;
        page += 1;
      }

      let results = all.filter((lead) => canAccessLeadRecord(lead, currentUser, scopeUserIds));
      let count   = results.length;

      const start = (pg - 1) * PAGE_SIZE;
      const end = start + PAGE_SIZE;

      setLeads(results.slice(start, end));
      setTotal(count);
      setCurrentPage(pg);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load follow-ups');
      setLeads([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeFilter, currentUser, scopeUserIds]);

  // Counts for stat pills — fetch separately for accurate numbers
  const [counts, setCounts] = useState({ overdue: 0, today: 0, upcoming: 0, all: 0 });

  const fetchCounts = useCallback(async () => {
    try {
      const td = todayStr();
      const all = [];
      let page = 1;
      while (page <= 50) {
        const res = await api.getLeads({ page, page_size: 100 });
        const data = res?.data;
        const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        all.push(...results);
        if (!data?.next || results.length === 0) break;
        page += 1;
      }
      const scoped = all.filter((lead) => canAccessLeadRecord(lead, currentUser, scopeUserIds));
      setCounts({
        overdue:  scoped.filter((lead) => lead.next_follow_up_date && lead.next_follow_up_date < td && lead.current_stage === 'FOLLOW_UP').length,
        today:    scoped.filter((lead) => lead.next_follow_up_date === td).length,
        upcoming: scoped.filter((lead) => lead.next_follow_up_date && lead.next_follow_up_date > td).length,
        all:      scoped.filter((lead) => lead.next_follow_up_date).length,
      });
    } catch (_) {}
  }, [currentUser, scopeUserIds]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    fetchLeads(1);
  }, [activeFilter, search, fetchLeads]); // eslint-disable-line

  const td = todayStr();

  // Client-side filter for display (backup when API doesn't support all params)
  const displayLeads = leads.filter(lead => {
    const fuDate = lead.next_follow_up_date;
    if (activeFilter === 'overdue')  return fuDate && fuDate < td;
    if (activeFilter === 'today')    return fuDate && fuDate === td;
    if (activeFilter === 'upcoming') return fuDate && fuDate > td;
    return true;
  });

  const FILTERS = [
    { key:'overdue',  label:'Overdue',  color:C.red,    bg:C.redL,    border:C.redB,    icon:<FiAlertCircle size={12}/> },
    { key:'today',    label:'Today',    color:'#92670C', bg:C.goldL,  border:'#F0D798', icon:<FiClock size={12}/>       },
    { key:'upcoming', label:'Upcoming', color:C.navy,   bg:C.navyL,   border:'rgba(13,59,94,0.20)', icon:<FiCalendar size={12}/> },
    { key:'all',      label:'All',      color:C.t2,     bg:C.borderL, border:C.border,  icon:<FiUser size={12}/>        },
  ];

  const countMap = { overdue: counts.overdue, today: counts.today, upcoming: counts.upcoming, all: counts.all };

  const hasFilters = !!search;
  const clearFilters = () => setSearch('');

  return (
    <>
      <style>{`
        @keyframes fuShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes fuSlideDown { from{opacity:0;transform:translateY(-5px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fuSpin { to{transform:rotate(360deg)} }
      `}</style>
      <Layout pageTitle="Follow-ups">
        <div style={{ display:'flex', flexDirection:'column', gap:14, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>

          {/* ══ HERO BANNER ══ */}
          <div style={{ borderRadius:14, padding: isMobile ? '14px 16px' : '20px 28px', background:'linear-gradient(120deg,#0D3B5E,#1A5276)', boxShadow:'0 4px 20px rgba(13,59,94,0.20)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.45)', fontWeight:700, letterSpacing:'0.10em', textTransform:'uppercase', marginBottom:5 }}>Lead Management</div>
              <h2 style={{ margin:0, fontSize:22, fontWeight:800, color:'#fff', letterSpacing:'-0.03em', lineHeight:1.2 }}>Follow-ups</h2>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:7, flexWrap:'wrap' }}>
                <span style={{ fontSize:13, color:'rgba(255,255,255,0.50)' }}>Track and manage all your follow-up activities</span>
                {counts.overdue > 0 && (
                  <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 11px', borderRadius:999, fontSize:12, fontWeight:700, background:'rgba(220,38,38,0.22)', border:'1px solid rgba(220,38,38,0.40)', color:'#FCA5A5' }}>
                    <FiAlertCircle size={11}/> {counts.overdue} overdue
                  </span>
                )}
              </div>
            </div>
            <button onClick={() => fetchLeads(currentPage, true)} disabled={refreshing} title="Refresh"
              style={{ width:38, height:38, borderRadius:9, background:'rgba(255,255,255,0.11)', border:'1px solid rgba(255,255,255,0.22)', color:'rgba(255,255,255,0.70)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, transition:'all 0.12s' }}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.20)'}
              onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.11)'}>
              <FiRefreshCw size={14} style={{ animation: refreshing ? 'fuSpin 1s linear infinite' : 'none' }}/>
            </button>
          </div>

          {/* ══ STAT PILLS ══ */}
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? 8 : 12 }}>
            {FILTERS.map(f => (
              <StatPill key={f.key}
                label={f.label}
                value={loading ? '—' : countMap[f.key]}
                color={f.color} bg={f.bg} border={f.border}
                active={activeFilter === f.key}
                onClick={() => setActiveFilter(f.key)}
              />
            ))}
          </div>

          {/* ══ FILTER BAR ══ */}
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, boxShadow:'0 2px 6px rgba(13,59,94,0.06)', padding:'12px 16px', display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>

            {/* Search */}
            <div style={{ position:'relative', flex:1, minWidth:200, maxWidth:340 }}>
              <FiSearch size={13} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4 }}/>
              <input type="text" placeholder="Search name, mobile, lead no..." value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width:'100%', height:36, border:`1.5px solid ${C.border}`, borderRadius:9, fontSize:13, fontFamily:'inherit', outline:'none', background:'#F9FAFB', color:C.t1, paddingLeft:34, paddingRight:12, boxSizing:'border-box', transition:'all 0.15s' }}
                onFocus={e=>{ e.target.style.borderColor=C.navy; e.target.style.background='#fff'; e.target.style.boxShadow='0 0 0 3px rgba(13,59,94,0.08)'; }}
                onBlur={e=>{ e.target.style.borderColor=C.border; e.target.style.background='#F9FAFB'; e.target.style.boxShadow='none'; }}
              />
            </div>

            {/* Active filter badge */}
            <div style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:20, border:`1.5px solid ${FILTERS.find(f=>f.key===activeFilter)?.border}`, background:FILTERS.find(f=>f.key===activeFilter)?.bg, fontSize:12.5, fontWeight:700, color:FILTERS.find(f=>f.key===activeFilter)?.color }}>
              {FILTERS.find(f=>f.key===activeFilter)?.icon}
              {FILTERS.find(f=>f.key===activeFilter)?.label} Follow-ups
            </div>

            <div style={{ flex:1 }}/>

            {/* Result count */}
            {!loading && (
              <span style={{ fontSize:12.5, color:C.t4, fontWeight:500 }}>
                {total} result{total !== 1 ? 's' : ''}
              </span>
            )}

            {hasFilters && (
              <button onClick={clearFilters} style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'7px 13px', borderRadius:9, fontSize:12.5, fontWeight:700, cursor:'pointer', fontFamily:'inherit', border:'1.5px solid #FECACA', background:'#FEF2F2', color:C.red }}>
                <FiX size={11}/> Clear
              </button>
            )}
          </div>

          {/* ══ TABLE ══ */}
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)', animation:'fuSlideDown 0.18s ease both' }}>

            {loading && (
              <div style={{ padding:'20px', display:'flex', flexDirection:'column', gap:10 }}>
                {[1,2,3,4,5].map(i => <Shimmer key={i}/>)}
              </div>
            )}

            {!loading && displayLeads.length === 0 && (
              <div style={{ textAlign:'center', padding:'64px 24px' }}>
                <div style={{ width:64, height:64, borderRadius:'50%', background:C.navyL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px', border:`1.5px solid rgba(13,59,94,0.12)` }}>
                  <FiClock size={26} style={{ color:C.navy, opacity:0.55 }}/>
                </div>
                <div style={{ fontSize:15, fontWeight:800, color:C.t1, marginBottom:7 }}>
                  {activeFilter === 'overdue'  ? 'No overdue follow-ups' :
                   activeFilter === 'today'    ? 'No follow-ups scheduled for today' :
                   activeFilter === 'upcoming' ? 'No upcoming follow-ups' :
                   'No follow-ups found'}
                </div>
                <div style={{ fontSize:13.5, color:C.t4 }}>
                  {activeFilter === 'today' ? 'You\'re all caught up for today!' : 'Try a different filter'}
                </div>
              </div>
            )}

            {!loading && displayLeads.length > 0 && (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:820 }}>
                  <thead>
                    <tr style={{ background:'#F8FAFC', borderBottom:`1.5px solid ${C.border}` }}>
                      {[
                        { h:'Lead No.',     align:'left'   },
                        { h:'Customer',     align:'left'   },
                        { h:'Product',      align:'left'   },
                        { h:'Loan Amount',  align:'right'  },
                        { h:'Stage',        align:'left'   },
                        { h:'Follow-up',    align:'left'   },
                        { h:'Action',       align:'center' },
                      ].map(({h,align}) => (
                        <th key={h} style={{ padding:'11px 16px', fontSize:10.5, fontWeight:800, color:'#1F2937', textTransform:'uppercase', letterSpacing:'0.08em', whiteSpace:'nowrap', textAlign:align }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayLeads.map((lead, idx) => (
                      <FURow key={lead.id} lead={lead} navigate={navigate} isLast={idx === displayLeads.length - 1}/>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && total > PAGE_SIZE && (
              <Pagination currentPage={currentPage} totalCount={total} pageSize={PAGE_SIZE} onPageChange={pg => fetchLeads(pg)}/>
            )}

            {/* Footer */}
            {!loading && displayLeads.length > 0 && (
              <div style={{ padding:'10px 20px', borderTop:`1px solid ${C.borderL}`, fontSize:12.5, color:C.t4, background:'#FAFBFC', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span>
                  Showing <strong style={{ color:C.t2 }}>{displayLeads.length}</strong> of <strong style={{ color:C.t2 }}>{total}</strong> follow-up{total !== 1 ? 's' : ''}
                </span>
                {counts.overdue > 0 && activeFilter !== 'overdue' && (
                  <button onClick={() => setActiveFilter('overdue')} style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:12, fontWeight:700, color:C.red, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit' }}>
                    <FiAlertCircle size={11}/> {counts.overdue} overdue — view them
                  </button>
                )}
              </div>
            )}
          </div>

        </div>
      </Layout>
    </>
  );
}
